import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase.js';
import { IMAGE_MAX_SIZE, IMAGE_ALLOWED_TYPES } from '../config/constants.js';
import { devLog, devError, devWarn } from './logger.js';

/**
 * 画像アップロード用ユーティリティ
 */

/**
 * 画像ファイルのバリデーション
 * @param {File} file - アップロードするファイル
 * @returns {{valid: boolean, error: string|null}}
 */
export function validateImageFile(file) {
  if (!file) {
    return { valid: false, error: 'ファイルが選択されていません' };
  }

  // ファイルサイズチェック
  if (file.size > IMAGE_MAX_SIZE) {
    const sizeMB = IMAGE_MAX_SIZE / 1024 / 1024;
    return { valid: false, error: `ファイルサイズは${sizeMB}MB以下にしてください` };
  }

  // ファイル形式チェック
  if (!IMAGE_ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, error: '対応している画像形式: JPEG, PNG, GIF, WebP' };
  }

  return { valid: true, error: null };
}

/**
 * クリニックのロゴ画像をアップロード
 * @param {string} clinicId - クリニックID
 * @param {File} file - アップロードする画像ファイル
 * @returns {Promise<string>} - アップロードされた画像のURL
 */
export async function uploadClinicLogo(clinicId, file) {
  const validation = validateImageFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  try {
    // ファイル名を生成（タイムスタンプ付き）
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const filename = `logo_${timestamp}.${fileExtension}`;
    
    // Storage参照を作成
    const storageRef = ref(storage, `clinics/${clinicId}/logo/${filename}`);
    
    // アップロード
    await uploadBytes(storageRef, file);
    
    // ダウンロードURLを取得
    const downloadURL = await getDownloadURL(storageRef);
    
    devLog('クリニックロゴアップロード完了:', downloadURL);
    return downloadURL;
  } catch (error) {
    devError('クリニックロゴアップロードエラー:', error);
    throw new Error('画像のアップロードに失敗しました: ' + error.message);
  }
}

/**
 * ユーザーのアバター画像をアップロード
 * 【注意】現在未使用 - 将来のユーザープロフィール機能で使用予定
 * @param {string} userId - ユーザーID
 * @param {File} file - アップロードする画像ファイル
 * @returns {Promise<string>} - アップロードされた画像のURL
 */
export async function uploadUserAvatar(userId, file) {
  const validation = validateImageFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  try {
    // ファイル名を生成（タイムスタンプ付き）
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const filename = `avatar_${timestamp}.${fileExtension}`;
    
    // Storage参照を作成
    const storageRef = ref(storage, `users/${userId}/avatar/${filename}`);
    
    // アップロード
    await uploadBytes(storageRef, file);
    
    // ダウンロードURLを取得
    const downloadURL = await getDownloadURL(storageRef);
    
    devLog('ユーザーアバターアップロード完了:', downloadURL);
    return downloadURL;
  } catch (error) {
    devError('ユーザーアバターアップロードエラー:', error);
    throw new Error('画像のアップロードに失敗しました: ' + error.message);
  }
}

/**
 * Firebase Storage の download URL から Storage パスを抽出
 * @param {string} imageUrl - getDownloadURL で取得した URL
 * @returns {string|null} - Storage 内のパス（抽出できない場合は null）
 */
function getStoragePathFromDownloadUrl(imageUrl) {
  if (!imageUrl || typeof imageUrl !== 'string') return null;
  try {
    const match = imageUrl.match(/\/o\/(.+?)(\?|$)/);
    if (!match) return null;
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/**
 * Storage上の画像を削除（download URL または Storage パスに対応）
 * @param {string} imageUrl - 削除する画像の download URL または Storage パス
 * @returns {Promise<void>}
 */
export async function deleteImage(imageUrl) {
  if (!imageUrl) {
    return;
  }

  try {
    const path = imageUrl.startsWith('http') ? getStoragePathFromDownloadUrl(imageUrl) : imageUrl;
    const storagePath = path || imageUrl;
    const imageRef = ref(storage, storagePath);
    await deleteObject(imageRef);
    devLog('画像削除完了:', storagePath);
  } catch (error) {
    if (error.code === 'storage/object-not-found') {
      devWarn('削除対象の画像が見つかりません:', imageUrl);
      return;
    }
    devError('画像削除エラー:', error);
    throw error;
  }
}

/**
 * 画像ファイルをプレビュー用のData URLに変換
 * @param {File} file - 画像ファイル
 * @returns {Promise<string>} - Data URL
 */
export function getImagePreviewUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

